import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Building2, Briefcase, MapPin, Video, Phone, Check, Clock, Loader2 } from 'lucide-react';
import { interviewsAPI } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { FORMATOS, fmtDateTime, placeHref, isPast } from './interviewUtils';

const ICONS = { presencial: MapPin, online: Video, telefone: Phone };
const CAND_STATUS = {
  AGUARDANDO: { label: 'Confirme sua presença', cls: 'bg-amber-100 text-amber-800' },
  CONFIRMADA: { label: 'Confirmada', cls: 'bg-emerald-100 text-emerald-800' },
  NOVO_HORARIO: { label: 'Aguardando a empresa', cls: 'bg-orange-100 text-orange-800' },
  CANCELADA: { label: 'Cancelada', cls: 'bg-slate-200 text-slate-600' },
};

const SuggestForm = ({ iv, onDone }) => {
  const [dt, setDt] = useState('');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!dt) { toast.error('Escolha a data e a hora'); return; }
    setBusy(true);
    try { await interviewsAPI.suggest(iv.id, { data_hora: new Date(dt).toISOString(), motivo }); toast.success('Sugestão enviada para a empresa'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao sugerir horário'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-lg bg-slate-50 p-3" data-testid={`suggest-form-${iv.id}`}>
      <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid={`suggest-datetime-${iv.id}`} />
      <Textarea rows={2} placeholder="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid={`suggest-reason-${iv.id}`} />
      <Button size="sm" onClick={send} disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid={`suggest-send-${iv.id}`}>
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enviar sugestão
      </Button>
    </div>
  );
};

const MyInterviewCard = ({ iv, onChange }) => {
  const [suggesting, setSuggesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[iv.formato] || MapPin;
  const confirm = async () => {
    setBusy(true);
    try { await interviewsAPI.confirm(iv.id); toast.success('Presença confirmada!'); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao confirmar'); } finally { setBusy(false); }
  };
  const canAct = ['AGUARDANDO', 'CONFIRMADA'].includes(iv.status) && !isPast(iv.data_hora);
  return (
    <Card className={`border-0 shadow-sm ${iv.status === 'AGUARDANDO' ? 'ring-2 ring-amber-300' : ''}`} data-testid={`my-interview-${iv.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold text-slate-900 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-cyan-600" />{fmtDateTime(iv.data_hora)}</p>
          <Badge className={CAND_STATUS[iv.status]?.cls} data-testid={`my-interview-status-${iv.id}`}>{CAND_STATUS[iv.status]?.label}</Badge>
        </div>
        <p className="text-sm text-slate-700 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-400" />{iv.company_name}</p>
        <p className="text-sm text-slate-700 flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-slate-400" />{iv.job_title}</p>
        {iv.status !== 'CANCELADA' && (
          <a href={placeHref(iv.formato, iv.local)} target="_blank" rel="noreferrer" className="text-sm text-cyan-700 flex items-center gap-1.5 break-all" data-testid={`my-interview-place-${iv.id}`}>
            <Icon className="w-4 h-4 shrink-0" />{FORMATOS[iv.formato]?.label}: {iv.local}
          </a>
        )}
        {iv.observacoes && <p className="text-xs text-slate-500">{iv.observacoes}</p>}
        {iv.status === 'NOVO_HORARIO' && iv.sugestao_data_hora && (
          <p className="text-xs text-orange-800 flex items-center gap-1"><Clock className="w-3 h-3" />Você sugeriu {fmtDateTime(iv.sugestao_data_hora)}</p>
        )}
        {canAct && (
          <div className="flex gap-2 pt-1">
            {iv.status === 'AGUARDANDO' && (
              <Button size="sm" onClick={confirm} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`my-interview-confirm-${iv.id}`}>
                <Check className="w-4 h-4 mr-1" />Confirmar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setSuggesting((s) => !s)} className="flex-1" data-testid={`my-interview-suggest-${iv.id}`}>
              Pedir outro horário
            </Button>
          </div>
        )}
        {suggesting && canAct && <SuggestForm iv={iv} onDone={() => { setSuggesting(false); onChange(); }} />}
      </CardContent>
    </Card>
  );
};

export const MyInterviewsList = () => {
  const [items, setItems] = useState([]);
  const load = () => interviewsAPI.mine().then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const visible = items.filter((i) => i.status !== 'CANCELADA' || new Date(i.data_hora) > new Date());
  if (!visible.length) return null;
  return (
    <div className="space-y-3" data-testid="my-interviews-list">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-cyan-600" />Minhas entrevistas</h2>
      {visible.map((iv) => <MyInterviewCard key={iv.id} iv={iv} onChange={load} />)}
    </div>
  );
};
